/**
 * ApprovalDetailPage — 결재 단건 상세.
 *
 * 【역할】
 * - URL :id 기반 단건 결재 조회 + 결재선 Timeline + 결재 액션
 * - 현재 사용자가 처리 차례면 Approve/Reject 버튼 활성화
 *
 * 【주요 기능】
 * - Descriptions 로 메타 표시 (title/description/requester/execType/ref)
 * - Timeline 으로 결재선 시각화 (각 step 의 role/assignee/status/comment)
 * - 내 차례면 Approve/Reject + 코멘트 입력
 * - 뒤로가기 버튼 → 목록
 *
 * 【사용 훅】
 * - useTransaction
 * - useParams / useNavigate
 * - useLabel (t)
 *
 * 【Backend RPC serviceName】
 * - approval/searchById  — 단건 상세 + 결재선
 * - approval/act         — 결재 처리 (approve/reject + comment)
 *
 * 【DataSet 구조】
 * - 없음 (단건 + steps[] 을 state 로 관리)
 *
 * 【사용처】 router/index.tsx — `{ path: 'approval/:id' }`
 * 【포팅 원본】 react-fastapi 신규 화면 (7-role 승인 체계)
 */
import { useEffect, useState } from 'react'
import { Button, Card, Descriptions, Input, Space, Tag, Timeline, Typography, message } from 'antd'
import { ArrowLeftOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { useTransaction } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import PageContainer from '@/components/common/PageContainer'

const { Title, Text } = Typography

interface Approval {
  approvalId: number
  title: string
  description?: string
  requesterId: string
  status: string
  execType: string
  refDomain?: string
  refId?: string
  createdAt?: string
}

interface Step {
  stepId: number
  seq: number
  role: string
  assigneeId: string
  assigneeName?: string
  status: string
  comment?: string
  actedAt?: string
  actedBy?: string
}

const STATUS_COLOR: Record<string, string> = {
  PENDING: 'processing',
  APPROVED: 'success',
  REJECTED: 'error',
  WITHDRAWN: 'default',
  DELEGATED: 'warning',
  NOTIFIED: 'blue',
  SKIPPED: 'default'
}

/**
 * ApprovalDetailPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function ApprovalDetailPage() {
  const { t } = useLabel()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { transaction } = useTransaction()
  const [header, setHeader] = useState<Approval | null>(null)
  const [steps, setSteps] = useState<Step[]>([])
  const [comment, setComment] = useState('')

  const load = async () => {
    const res = await transaction({
      id: 'detail',
      service: 'approval/searchById',
      action: 'search',
      inds: { ds_search: { approvalId: Number(id) } }
    })
    const data = res.data as
      | { ds_approval?: { rows?: Approval[] }; ds_step?: { rows?: Step[] } }
      | undefined
    setHeader(data?.ds_approval?.rows?.[0] ?? null)
    setSteps(data?.ds_step?.rows ?? [])
  }

  useEffect(() => {
    if (id) load()
  }, [id])

  if (!header) return <PageContainer>{t('LBL_LOADING', '로딩 중...')}</PageContainer>

  const myStep = steps.find((s) => s.status === 'PENDING' && s.assigneeId === 'admin') // TODO: authStore

  const act = async (action: 'APPROVE' | 'REJECT' | 'DELEGATE' | 'WITHDRAW') => {
    if (action !== 'WITHDRAW' && !myStep) {
      void message.warning(t('MSG_NO_PENDING_STEP', '처리 가능한 단계가 없습니다.'))
      return
    }
    const res = await transaction({
      id: 'act',
      service: 'approval/act',
      action: 'save',
      inds: {
        ds_action: {
          rows: [
            {
              approvalId: header.approvalId,
              stepId: myStep?.stepId,
              action,
              comment
            }
          ]
        }
      }
    })
    if (res.success) {
      void message.success(t('MSG_SAVE_SUCCESS', '저장되었습니다.'))
      setComment('')
      load()
    }
  }

  return (
    <PageContainer style={{ overflow: 'auto' }}>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          {t('BTN_BACK', '뒤로')}
        </Button>
      </Space>

      <Title level={3} style={{ marginTop: 0 }}>
        #{header.approvalId} {header.title}
      </Title>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label={t('LBL_REQUESTER', '기안자')}>
            {header.requesterId}
          </Descriptions.Item>
          <Descriptions.Item label={t('LBL_STATUS', '상태')}>
            <Tag color={STATUS_COLOR[header.status] || 'default'}>{header.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('LBL_EXEC_TYPE', '진행 방식')}>
            {header.execType}
          </Descriptions.Item>
          <Descriptions.Item label={t('LBL_CREATED_AT', '생성일시')}>
            {header.createdAt}
          </Descriptions.Item>
          <Descriptions.Item label={t('LBL_REF_DOMAIN', '참조 도메인')}>
            {header.refDomain || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('LBL_REF_ID', '참조 ID')}>
            {header.refId || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('LBL_DESCRIPTION', '설명')} span={2}>
            {header.description || '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title={t('LBL_APPROVAL_LINE', '결재선')} style={{ marginBottom: 12 }}>
        <Timeline
          items={steps.map((s) => ({
            color:
              s.status === 'APPROVED'
                ? 'green'
                : s.status === 'REJECTED'
                  ? 'red'
                  : s.status === 'PENDING'
                    ? 'blue'
                    : 'gray',
            children: (
              <div>
                <Space>
                  <Tag>{s.role}</Tag>
                  <Text strong>{s.assigneeName || s.assigneeId}</Text>
                  <Tag color={STATUS_COLOR[s.status] || 'default'}>{s.status}</Tag>
                  {s.actedAt && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {s.actedAt}
                    </Text>
                  )}
                </Space>
                {s.comment && <div style={{ marginTop: 4, fontSize: 12 }}>{s.comment}</div>}
              </div>
            )
          }))}
        />
      </Card>

      {(myStep || header.requesterId === 'admin') && header.status === 'PENDING' && (
        <Card size="small" title={t('LBL_ACTION', '작업')}>
          <Input.TextArea
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('PH_COMMENT', '의견을 입력하세요')}
            style={{ marginBottom: 8 }}
          />
          <Space>
            {myStep && (
              <>
                <Button type="primary" icon={<CheckOutlined />} onClick={() => act('APPROVE')}>
                  {t('BTN_APPROVE_ACTION', '승인')}
                </Button>
                <Button danger icon={<CloseOutlined />} onClick={() => act('REJECT')}>
                  {t('BTN_REJECT', '반려')}
                </Button>
              </>
            )}
            {header.requesterId === 'admin' && (
              <Button onClick={() => act('WITHDRAW')}>{t('BTN_WITHDRAW', '철회')}</Button>
            )}
          </Space>
        </Card>
      )}
    </PageContainer>
  )
}
